import React, { useState, useEffect } from 'react'
import {
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  TextArea,
} from '@vibe/core'
import { Check, Close, Alert, Retry } from '@vibe/icons'
import { ApiService } from '../../services/api'
import { getErrorMessage } from '../../utils/errorHandling'
import { useAuth } from '../../contexts/AuthContext'
import type {
  SolicitudWorkflowState,
  ApprovalRecord,
  WorkflowLogic,
} from '../../types'

interface WorkflowViewerProps {
  solicitudId: string
  onClose?: () => void
  refreshInterval?: number // Auto-refresh interval in milliseconds
}

interface WorkflowStatusSummary {
  id_solicitud: string
  has_workflow: boolean
  workflow_id?: string
  approval_status: 'pending' | 'approved' | 'rejected'
  is_complete: boolean
  pending_approvals: number
  total_approvals: number
  approvals: ApprovalRecord[]
  created_at?: string
  updated_at?: string
  completed_at?: string
  completion_percentage: number
  next_required_approvals: string[]
  approved_users: string[]
  rejected_users: string[]
  pending_users: string[]
}

const WorkflowViewer: React.FC<WorkflowViewerProps> = ({
  solicitudId,
  onClose,
  refreshInterval = 30000, // 30 seconds default
}) => {
  const [workflowStatus, setWorkflowStatus] =
    useState<WorkflowStatusSummary | null>(null)
  const [users, setUsers] = useState<any[]>([]) // Store users for name mapping
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  // Approval/rejection state
  const [showApprovalModal, setShowApprovalModal] = useState(false)
  const [approvalAction, setApprovalAction] = useState<
    'approve' | 'reject' | null
  >(null)
  const [approvalComments, setApprovalComments] = useState('')
  const [submittingApproval, setSubmittingApproval] = useState(false)

  const apiService = new ApiService()
  const { user } = useAuth()

  const fetchWorkflowStatus = async () => {
    try {
      setError(null)

      // Load workflow status and users in parallel
      const [workflowResponse, usersResponse] = await Promise.all([
        apiService.getWorkflowStatus(solicitudId),
        apiService.getUsers(),
      ])

      setWorkflowStatus(workflowResponse.data)
      setUsers(usersResponse.data.items || [])
      setLastUpdated(new Date())
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
      console.error('Error fetching workflow status:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkflowStatus()

    // Set up auto-refresh if interval is provided
    let intervalId: NodeJS.Timeout | null = null
    if (refreshInterval > 0) {
      intervalId = setInterval(fetchWorkflowStatus, refreshInterval)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [solicitudId, refreshInterval])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
        return <Check className="h-4 w-4 text-green-600" />
      case 'rejected':
        return <Close className="h-4 w-4 text-red-600" />
      case 'pending':
        return <i className="fa-solid fa-clock h-4 w-4 text-yellow-600" />
      default:
        return <Alert className="h-4 w-4 text-gray-400" />
    }
  }

  const getStatusBadge = (status: string) => {
    const badgeClasses = {
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      pending: 'bg-yellow-100 text-yellow-800',
    }

    const labels = {
      approved: 'Aprobado',
      rejected: 'Rechazado',
      pending: 'Pendiente',
    }

    return (
      <Badge
        className={
          badgeClasses[status as keyof typeof badgeClasses] ||
          'bg-gray-100 text-gray-800'
        }
      >
        {labels[status as keyof typeof labels] || status}
      </Badge>
    )
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  // Get user name by ID
  const getUserName = (userId: string): string => {
    const foundUser = users.find(u => u.id === userId)
    return foundUser ? foundUser.name || foundUser.email : userId
  }

  // Check if current user can approve this solicitud
  const canCurrentUserApprove = () => {
    if (!user || !workflowStatus) return false

    const userId = user.id
    return workflowStatus.next_required_approvals.includes(userId)
  }

  // Handle approval action
  const handleApprovalAction = (action: 'approve' | 'reject') => {
    setApprovalAction(action)
    setApprovalComments('')
    setShowApprovalModal(true)
  }

  // Submit approval/rejection
  const submitApproval = async () => {
    if (!approvalAction) return

    try {
      setSubmittingApproval(true)

      if (approvalAction === 'approve') {
        await apiService.approveSolicitud(solicitudId, approvalComments)
      } else {
        await apiService.rejectSolicitud(solicitudId, approvalComments)
      }

      // Refresh workflow status
      await fetchWorkflowStatus()

      // Close modal
      setShowApprovalModal(false)
      setApprovalAction(null)
      setApprovalComments('')
    } catch (err) {
      const errorMessage = getErrorMessage(err)
      setError(errorMessage)
      console.error('Error submitting approval:', err)
    } finally {
      setSubmittingApproval(false)
    }
  }

  // Cancel approval modal
  const cancelApproval = () => {
    setShowApprovalModal(false)
    setApprovalAction(null)
    setApprovalComments('')
  }

  const renderApprovalRecord = (approval: ApprovalRecord, index: number) => {
    const isNextRequired = workflowStatus?.next_required_approvals.includes(
      approval.user_id
    )

    return (
      <div
        key={`${approval.user_id}-${index}`}
        className={`flex items-center space-x-3 p-3 rounded-lg border ${
          isNextRequired
            ? 'border-blue-200 bg-blue-50'
            : 'border-gray-200 bg-gray-50'
        }`}
      >
        <div className="h-10 w-10 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center">
          <i className="fa-solid fa-user h-5 w-5" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium text-gray-900 truncate">
              {getUserName(approval.user_id)}
            </p>
            {isNextRequired && (
              <Badge className="bg-blue-100 text-blue-800 text-xs">
                Requerido
              </Badge>
            )}
          </div>

          {approval.timestamp && (
            <p className="text-xs text-gray-400">
              {formatDate(approval.timestamp)}
            </p>
          )}
          {approval.comments && (
            <p className="text-xs text-gray-600 mt-1 italic">
              "{approval.comments}"
            </p>
          )}
        </div>

        <div className="flex items-center space-x-2">
          {getStatusIcon(approval.status)}
          {getStatusBadge(approval.status)}
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="card w-full max-w-4xl mx-auto">
        <div className="p-6">
          <div className="flex items-center space-x-2">
            <Retry className="h-5 w-5 animate-spin" />
            <h3 className="text-lg font-semibold">
              Cargando estado del flujo de aprobación...
            </h3>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card w-full max-w-4xl mx-auto">
        <div className="p-6">
          <div className="flex items-center space-x-2 text-red-600 mb-4">
            <Alert className="h-5 w-5" />
            <h3 className="text-lg font-semibold">
              Error al cargar el flujo de aprobación
            </h3>
          </div>
          <p className="text-red-600 mb-4">{error}</p>
          <div className="flex space-x-2">
            <Button onClick={fetchWorkflowStatus} className="btn-secondary">
              <Retry className="h-4 w-4 mr-2" />
              Reintentar
            </Button>
            {onClose && (
              <Button onClick={onClose} className="btn-secondary">
                Cerrar
              </Button>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (!workflowStatus) {
    return (
      <div className="card w-full max-w-4xl mx-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Flujo de Aprobación</h3>
          <p className="text-gray-600">
            No se encontró información del flujo de aprobación.
          </p>
          {onClose && (
            <Button onClick={onClose} className="btn-secondary mt-4">
              Cerrar
            </Button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="card w-full max-w-4xl mx-auto">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h3 className="text-lg font-semibold">Flujo de Aprobación</h3>
            {getStatusIcon(workflowStatus.approval_status)}
          </div>
          <div className="flex items-center space-x-2">
            {lastUpdated && (
              <span
                className="text-xs text-gray-500"
                title={`Última actualización: ${lastUpdated.toLocaleString()}`}
              >
                Actualizado: {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button
              onClick={fetchWorkflowStatus}
              className="btn-secondary text-sm"
            >
              <Retry className="h-4 w-4" />
            </Button>
            {onClose && (
              <Button onClick={onClose} className="btn-secondary text-sm">
                ✕
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Overall Status */}
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            <div>
              <p className="text-sm font-medium text-gray-900">
                Estado General
              </p>
              <p className="text-xs text-gray-500">
                Solicitud ID: {workflowStatus.id_solicitud}
              </p>
            </div>
            {getStatusBadge(workflowStatus.approval_status)}
          </div>

          {/* Completion percentage - Only show if not rejected */}
          {workflowStatus.approval_status !== 'rejected' && (
            <div className="text-right">
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(workflowStatus.completion_percentage)}%
              </p>
              <p className="text-xs text-gray-500">Completado</p>
            </div>
          )}
        </div>

        {/* Progress Bar - Only show if not rejected */}
        {workflowStatus.approval_status !== 'rejected' && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Progreso de Aprobación</span>
              <span>
                {workflowStatus.total_approvals -
                  workflowStatus.pending_approvals}{' '}
                de {workflowStatus.total_approvals} aprobaciones
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${workflowStatus.completion_percentage}%` }}
              />
            </div>
          </div>
        )}

        {/* Auto-approved case */}
        {!workflowStatus.has_workflow && (
          <div className="text-center py-8">
            <Check className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aprobación Automática
            </h3>
            <p className="text-gray-600">
              Esta solicitud no requiere un flujo de aprobación y fue aprobada
              automáticamente.
            </p>
          </div>
        )}

        {/* Workflow with approvals */}
        {workflowStatus.has_workflow && (
          <>
            {/* Approval Records */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">
                Aprobaciones Requeridas
              </h3>

              <div className="space-y-3">
                {workflowStatus.approvals.map((approval, index) =>
                  renderApprovalRecord(approval, index)
                )}
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Timeline */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Cronología</h3>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Creado:</span>
                  <span>{formatDate(workflowStatus.created_at)}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Última actualización:</span>
                  <span>{formatDate(workflowStatus.updated_at)}</span>
                </div>

                {workflowStatus.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Completado:</span>
                    <span>{formatDate(workflowStatus.completed_at)}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Next Steps - Only show if not rejected */}
            {workflowStatus.next_required_approvals.length > 0 &&
              workflowStatus.approval_status !== 'rejected' && (
                <>
                  <hr className="border-gray-200" />
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      Próximos Pasos Requeridos
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-800 mb-2">
                        Las siguientes aprobaciones son necesarias para
                        continuar:
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {workflowStatus.next_required_approvals.map(userId => {
                          const approval = workflowStatus.approvals.find(
                            a => a.user_id === userId
                          )
                          return (
                            <li key={userId} className="text-sm text-blue-700">
                              {getUserName(userId)}
                            </li>
                          )
                        })}
                      </ul>

                      {/* Approval Actions for Current User */}
                      {canCurrentUserApprove() && (
                        <div className="mt-4 pt-4 border-t border-blue-200">
                          <p className="text-sm text-blue-800 mb-3 font-medium">
                            Puedes aprobar o rechazar esta solicitud:
                          </p>
                          <div className="flex space-x-3">
                            <Button
                              onClick={() => handleApprovalAction('approve')}
                              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                            >
                              <Check className="h-4 w-4 mr-2 text-white" />
                              Aprobar
                            </Button>
                            <Button
                              onClick={() => handleApprovalAction('reject')}
                              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                            >
                              <Close className="h-4 w-4 mr-2 text-white" />
                              Rechazar
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
          </>
        )}
      </div>

      {/* Approval/Rejection Modal */}
      {showApprovalModal && (
        <Modal show={true} onClose={cancelApproval}>
          <ModalHeader>
            <h3 className="text-lg font-semibold">
              {approvalAction === 'approve'
                ? 'Aprobar Solicitud'
                : 'Rechazar Solicitud'}
            </h3>
          </ModalHeader>
          <ModalContent>
            <div className="space-y-4">
              <p className="text-gray-700">
                {approvalAction === 'approve'
                  ? '¿Estás seguro de que deseas aprobar esta solicitud de trabajo?'
                  : '¿Estás seguro de que deseas rechazar esta solicitud de trabajo?'}
              </p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comentarios{' '}
                  {approvalAction === 'reject'
                    ? '(requeridos)'
                    : '(opcionales)'}
                </label>
                <TextArea
                  value={approvalComments}
                  onChange={e => setApprovalComments(e.target.value)}
                  placeholder={
                    approvalAction === 'approve'
                      ? 'Agrega comentarios sobre la aprobación...'
                      : 'Explica las razones del rechazo...'
                  }
                  rows={4}
                  className="w-full"
                />
              </div>
            </div>
          </ModalContent>
          <ModalFooter>
            <div className="flex justify-end space-x-3">
              <Button
                onClick={cancelApproval}
                disabled={submittingApproval}
                className="px-4 py-2 text-sm font-medium bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancelar
              </Button>
              <Button
                onClick={submitApproval}
                disabled={
                  submittingApproval ||
                  (approvalAction === 'reject' && !approvalComments.trim())
                }
                className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                  approvalAction === 'approve'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {submittingApproval ? (
                  <>
                    <Retry className="h-4 w-4 mr-2 animate-spin text-white" />
                    Procesando...
                  </>
                ) : (
                  <>
                    {approvalAction === 'approve' ? (
                      <Check className="h-4 w-4 mr-2 text-white" />
                    ) : (
                      <Close className="h-4 w-4 mr-2 text-white" />
                    )}
                    {approvalAction === 'approve' ? 'Aprobar' : 'Rechazar'}
                  </>
                )}
              </Button>
            </div>
          </ModalFooter>
        </Modal>
      )}
    </div>
  )
}

export default WorkflowViewer
